import { useEffect, useState } from 'react';
import Icon from '@components/common/atoms/Icon';
import { APP_BUILD } from '@/config/appVersion';

const CHECK_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Shows a "new version deployed" popup when /version.json (written by each build, served no-store)
 * no longer matches the build this tab is running, or when a lazy chunk of the old build fails to
 * load. Reload clears any Cache Storage and reloads; index.html is no-store and assets are hashed,
 * so the reload always picks up the new build.
 */
const UpdatePrompt = () => {
  const [show, setShow] = useState(false);
  const [newVersion, setNewVersion] = useState<string | null>(null);

  useEffect(() => {
    if (!import.meta.env.PROD) return;

    const check = async () => {
      try {
        const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const { buildId, version } = await res.json();
        if (buildId && buildId !== APP_BUILD.buildId) {
          setNewVersion(version ?? null);
          setShow(true);
        }
      } catch {
        // offline or mid-deploy: try again next time
      }
    };
    const onVisible = () => document.visibilityState === 'visible' && check();
    const onChunkError = () => setShow(true);

    check();
    const timer = window.setInterval(check, CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('vite:preloadError', onChunkError);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('vite:preloadError', onChunkError);
    };
  }, []);

  const reload = async () => {
    try {
      if ('caches' in window) await Promise.all((await caches.keys()).map((k) => caches.delete(k)));
    } finally {
      window.location.reload();
    }
  };

  if (!show) return null;

  return (
    <div
      role="alertdialog"
      aria-live="polite"
      aria-label="New version available"
      className="fixed z-[70] inset-x-4 bottom-24 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:w-96 rounded-2xl bg-white dark:bg-gray-800 shadow-2xl border border-gray-200 dark:border-gray-700 p-4 animate-in fade-in slide-in-from-bottom-2 duration-200"
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 p-2 rounded-xl bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400">
          <Icon name="Sparkles" size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 dark:text-white">New features are here!</p>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            We&apos;ve deployed a new version. Reload to get the latest features and fixes.
          </p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            v{APP_BUILD.version}
            {newVersion && newVersion !== APP_BUILD.version && <> &rarr; v{newVersion}</>}
          </p>
          <div className="mt-3 flex gap-2">
            <button
              onClick={reload}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
            >
              <Icon name="RefreshCw" size={16} />
              Reload now
            </button>
            <button
              onClick={() => setShow(false)}
              className="px-4 py-2 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdatePrompt;
